import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, Download, Calendar, ArrowRightLeft, Filter, User, RefreshCw, FileText, X, Wallet, CheckCircle, Clock, Hash, CreditCard, Smartphone, UserCheck, Plus } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { bousanmSolideApi } from '../services/bousanmSolideApi';
import NewDepotModal from './NewDepotModal';

const TransactionsBS = () => {
  const { t } = useTranslation(['solide', 'common']);
  // États
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // État pour détails transaction
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // État pour nouveau dépôt
  const [newDepotOpen, setNewDepotOpen] = useState(false);

  // Filtres
  const [filters, setFilters] = useState({
    search: '',
    date_debut: '',
    date_fin: '',
    agent: '',
    montant_min: '',
    montant_max: ''
  });

  const [showFilters, setShowFilters] = useState(false);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const pageSize = 20;
      const data = await bousanmSolideApi.getTransactionsSolide({
        page,
        page_size: pageSize,
        period: 'year', // Show all transactions for current year by default
        ...filters
      });

      // La pagination DRF met les données dans 'results'
      const results = data.results || {};
      const rawTransactions = results.transactions || [];
      const metadata = results.metadata || {};

      // Map transactions response to UI expectations
      const mappedTransactions = rawTransactions.map(t => {
        let montant = t.montant;

        // If montant is missing but formatted_amount exists (e.g. "3,280.00 HTG"), parse it
        if ((montant === undefined || montant === null) && t.formatted_amount) {
          // Remove currency text (HTG, USD) and commas, then parse
          const numericPart = t.formatted_amount.replace(/[^0-9.-]+/g, "");
          montant = parseFloat(numericPart);
        }

        // Extract currency properly
        let devise = t.devise;
        if (!devise && t.formatted_amount) {
          // Extract from "3,280.00 HTG" -> "HTG"
          const parts = t.formatted_amount.split(' ');
          if (parts.length > 1) {
            devise = parts[parts.length - 1]; // Last part is usually currency
          }
        }

        return {
          ...t,
          date: t.created_at, // UI expects 'date'
          client_nom: t.client_nom || t.client_name || 'Client Inconnu', // Fix: API returns client_nom
          client_telephone: t.client_telephone || '',
          // plan: t.plan || 'SOL', // Remove default 'SOL', let it be undefined or null
          plan: t.plan, // Only show if explicitly provided (e.g. 'fos', 'espwa')
          agent: t.agent || t.agent_name || t.created_by_username || 'N/A', // Fix: API returns created_by_username for agent
          fait_par: t.created_by_username || t.agent_name || 'Système',
          montant: parseFloat(montant || 0), // Ensure it's a number
          devise: devise || '' // Don't hardcode default, let it be empty if unknown
        };
      });

      setTransactions(mappedTransactions);

      // Map metadata to stats
      setStats({
        total_montant_htg: metadata.total_amount || 0,
        total_montant_usd: 0,
        nombre_transactions: metadata.total_transactions || 0,
        moyenne_montant: metadata.total_transactions ? (metadata.total_amount / metadata.total_transactions) : 0
      });

      // Calcul du nombre de pages
      const totalCount = data.count || 0;
      setTotalPages(Math.ceil(totalCount / pageSize) || 1);

    } catch (error) {
      console.error("Erreur chargement transactions:", error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [page]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    setPage(1);
    fetchTransactions();
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      date_debut: '',
      date_fin: '',
      agent: '',
      montant_min: '',
      montant_max: ''
    });
    setPage(1);
    setTimeout(fetchTransactions, 100);
  };

  const openTransactionDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setDetailsOpen(true);
  };

  return (
    <div className="h-full w-full bg-gray-50/50 overflow-y-auto p-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{t('solide:transactionsPage.title')}</h1>
          <p className="text-xs text-gray-500">{t('solide:transactionsPage.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setNewDepotOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white h-8 text-xs">
            <Plus className="h-3.5 w-3.5 mr-2" />
            {t('solide:transactionsPage.buttons.newDepot')}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchTransactions} className="bg-white h-8 text-xs">
            <RefreshCw className={`h-3.5 w-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('solide:transactionsPage.buttons.refresh')}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className={`h-8 text-xs ${showFilters ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white'}`}>
            <Filter className="h-3.5 w-3.5 mr-2" />
            {t('solide:transactionsPage.buttons.filters')}
          </Button>
        </div>
      </div>

      {/* Statistiques Rapides */}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <Card className="bg-white border-l-4 border-l-green-500 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden">
            <CardContent className="p-3 flex items-center gap-4 relative z-10">
              <div className="h-10 w-10 rounded-full bg-green-50 flex items-center justify-center shrink-0 text-green-600 shadow-sm border border-green-100">
                <span className="font-bold text-sm">$</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-0.5">{t('solide:transactionsPage.stats.totalCollected')}</p>
                <div className="flex items-baseline gap-3">
                  <div className="flex items-baseline gap-1">
                    <span className="text-base font-bold text-gray-900">{(stats.total_montant_htg || 0).toLocaleString()}</span>
                    <span className="text-[10px] text-gray-400 font-medium">G</span>
                  </div>
                  <div className="w-px h-3 bg-gray-200"></div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-base font-bold text-emerald-600">{(stats.total_montant_usd || 0).toLocaleString()}</span>
                    <span className="text-[10px] text-emerald-500 font-medium">$</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-l-4 border-l-blue-500 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden">
            <CardContent className="p-3 flex items-center gap-4 relative z-10">
              <div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center shrink-0 text-blue-600 shadow-sm border border-blue-100">
                <ArrowRightLeft className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-0.5">{t('solide:transactionsPage.stats.transactions')}</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-xl font-bold text-gray-900">{(stats.nombre_transactions || 0).toLocaleString()}</span>
                  <span className="px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[9px] font-medium">{t('solide:transactionsPage.stats.active')}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-l-4 border-l-purple-500 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden">
            <CardContent className="p-3 flex items-center gap-4 relative z-10">
              <div className="h-10 w-10 rounded-full bg-purple-50 flex items-center justify-center shrink-0 text-purple-600 shadow-sm border border-purple-100">
                <span className="font-bold text-sm">%</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-0.5">{t('solide:transactionsPage.stats.averagePerTrans')}</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-xl font-bold text-gray-900">{(stats.moyenne_montant || 0).toLocaleString()}</span>
                  <span className="text-[10px] text-gray-400 font-medium">{t('solide:transactionsPage.stats.mixed')}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Zone de Filtres */}
      {showFilters && (
        <Card className="mb-6 bg-white border-none shadow-sm animate-in slide-in-from-top-2">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">{t('solide:transactionsPage.filters.search')}</label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                  <Input
                    name="search"
                    placeholder={t('solide:transactionsPage.filters.searchPlaceholder')}
                    value={filters.search}
                    onChange={handleFilterChange}
                    className="pl-9 bg-gray-50 border-gray-200"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">{t('solide:transactionsPage.filters.period')}</label>
                <div className="flex gap-2">
                  <Input type="date" name="date_debut" value={filters.date_debut} onChange={handleFilterChange} className="bg-gray-50 border-gray-200 text-xs" />
                  <Input type="date" name="date_fin" value={filters.date_fin} onChange={handleFilterChange} className="bg-gray-50 border-gray-200 text-xs" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">{t('solide:transactionsPage.filters.agent')}</label>
                <Input
                  name="agent"
                  placeholder={t('solide:transactionsPage.filters.agentPlaceholder')}
                  value={filters.agent}
                  onChange={handleFilterChange}
                  className="bg-gray-50 border-gray-200"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">{t('solide:transactionsPage.filters.amountRange')}</label>
                <div className="flex gap-2">
                  <Input type="number" name="montant_min" placeholder={t('solide:transactionsPage.filters.min')} value={filters.montant_min} onChange={handleFilterChange} className="bg-gray-50 border-gray-200" />
                  <Input type="number" name="montant_max" placeholder={t('solide:transactionsPage.filters.max')} value={filters.montant_max} onChange={handleFilterChange} className="bg-gray-50 border-gray-200" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-100">
              <Button variant="ghost" size="sm" onClick={resetFilters}>{t('solide:transactionsPage.filters.reset')}</Button>
              <Button size="sm" onClick={applyFilters}>{t('solide:transactionsPage.filters.apply')}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="bg-white border-none shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 text-gray-500 border-b font-medium text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3">{t('solide:transactionsPage.table.idDate')}</th>
                <th className="px-4 py-3">{t('solide:transactionsPage.table.client')}</th>
                <th className="px-4 py-3">{t('solide:transactionsPage.table.accountPlan')}</th>
                <th className="px-4 py-3">{t('solide:transactionsPage.table.agent')}</th>
                <th className="px-4 py-3 text-right">{t('solide:transactionsPage.table.amount')}</th>
                <th className="px-4 py-3 text-center">{t('solide:transactionsPage.table.details')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-4 py-12 text-center">
                    <div className="flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
                  </td>
                </tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-12 text-center text-gray-500">
                    {t('solide:transactionsPage.table.noTransactions')}
                  </td>
                </tr>
              ) : (
                transactions.map((transaction, i) => (
                  <tr
                    key={i}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => openTransactionDetails(transaction)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-mono text-xs text-gray-500 mb-0.5">#{transaction.id.substring(0, 8)}...</div>
                      <div className="text-gray-900 font-medium flex items-center gap-1.5">
                        <Calendar className="h-3 w-3 text-gray-400" />
                        {new Date(transaction.date).toLocaleDateString()}
                        <span className="text-xs text-gray-400">{new Date(transaction.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{transaction.client_nom}</div>
                      <div className="text-xs text-gray-500">{transaction.client_telephone || '-'}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="font-mono bg-gray-50">{transaction.compte_code}</Badge>
                        {transaction.plan && <span className="text-xs font-bold text-blue-600 uppercase">{transaction.plan}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col text-xs">
                        <span className="text-gray-900 font-medium flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {t('solide:transactionsPage.table.agent')}
                        </span>
                        <span className="text-gray-400">{t('solide:transactionsPage.table.doneBy', { name: transaction.fait_par })}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-bold ${transaction.devise === 'USD' ? 'text-emerald-600' : 'text-gray-900'}`}>{(transaction.montant || 0).toLocaleString()}</span>
                      <span className={`text-xs ml-1 ${transaction.devise === 'USD' ? 'text-emerald-500' : 'text-gray-500'}`}>{transaction.devise === 'USD' ? 'USD' : 'HTG'}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-none">{t('solide:transactionsPage.table.deposit')}</Badge>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="p-4 border-t border-gray-100 flex items-center justify-between bg-gray-50/50">
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => { e.stopPropagation(); setPage(p => Math.max(1, p - 1)); }}
              disabled={page === 1}
              className="bg-white"
            >
              {t('solide:transactionsPage.pagination.previous')}
            </Button>
            <span className="text-xs text-gray-500">{t('solide:transactionsPage.pagination.pageInfo', { current: page, total: totalPages })}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => { e.stopPropagation(); setPage(p => Math.min(totalPages, p + 1)); }}
              disabled={page === totalPages}
              className="bg-white"
            >
              {t('solide:transactionsPage.pagination.next')}
            </Button>
          </div>
        )}
      </Card>

      {/* Modal Détails Transaction */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <FileText className="h-5 w-5 text-blue-600" />
              {t('solide:transactionsPage.modal.title')}
            </DialogTitle>
          </DialogHeader>

          {selectedTransaction && (
            <div className="space-y-4 py-1">
              {/* En-tête Montant Compact */}
              <div className={`relative overflow-hidden rounded-lg bg-gradient-to-br border p-4 text-center ${selectedTransaction.devise === 'USD' ? 'from-emerald-50 to-green-50 border-emerald-100' : 'from-blue-50 to-indigo-50 border-blue-100'}`}>
                <div className="absolute top-0 right-0 p-2 opacity-5">
                  <Wallet className={`h-16 w-16 transform rotate-12 ${selectedTransaction.devise === 'USD' ? 'text-emerald-600' : 'text-blue-600'}`} />
                </div>

                <div className="relative z-10 flex flex-col items-center justify-center">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider mb-1 border ${selectedTransaction.devise === 'USD' ? 'bg-emerald-100/80 text-emerald-700 border-emerald-200/50' : 'bg-blue-100/80 text-blue-700 border-blue-200/50'}`}>
                    <CheckCircle className="h-2.5 w-2.5" /> {t('solide:transactionsPage.modal.success')}
                  </span>

                  <div className={`flex items-baseline justify-center gap-1 mb-1 ${selectedTransaction.devise === 'USD' ? 'text-emerald-700' : 'text-blue-700'}`}>
                    <span className="text-2xl font-bold tracking-tight">
                      {(selectedTransaction.montant || 0).toLocaleString()}
                    </span>
                    <span className={`text-sm font-semibold ${selectedTransaction.devise === 'USD' ? 'text-emerald-600/80' : 'text-blue-600/80'}`}>{selectedTransaction.devise === 'USD' ? 'USD' : 'HTG'}</span>
                  </div>

                  <div className={`flex items-center justify-center gap-2 text-[10px] ${selectedTransaction.devise === 'USD' ? 'text-emerald-600/80' : 'text-blue-600/80'}`}>
                    <Calendar className="h-2.5 w-2.5" />
                    <span>{new Date(selectedTransaction.date).toLocaleDateString()}</span>
                    <span className={`w-0.5 h-0.5 rounded-full ${selectedTransaction.devise === 'USD' ? 'bg-emerald-400' : 'bg-blue-400'}`}></span>
                    <Clock className="h-2.5 w-2.5" />
                    <span>{new Date(selectedTransaction.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                </div>
              </div>

              {/* Informations Grid Compact */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                {/* ID */}
                <div className="col-span-2 space-y-1">
                  <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                    <Hash className="h-2.5 w-2.5" /> {t('solide:transactionsPage.modal.id')}
                  </label>
                  <div className="bg-gray-50 rounded border border-gray-100 p-1.5">
                    <code className="text-[10px] text-gray-600 font-mono block select-all truncate">{selectedTransaction.id}</code>
                  </div>
                </div>

                {/* Compte */}
                <div className="space-y-0.5">
                  <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                    <CreditCard className="h-2.5 w-2.5" /> {t('solide:transactionsPage.modal.account')}
                  </label>
                  <div className="flex items-baseline gap-2">
                    <p className="text-xs font-bold text-gray-900">{selectedTransaction.compte_code}</p>
                    {selectedTransaction.plan && <span className="text-[9px] px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded border border-blue-100 uppercase font-medium">{selectedTransaction.plan}</span>}
                  </div>
                </div>

                {/* Client */}
                <div className="space-y-0.5">
                  <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                    <User className="h-2.5 w-2.5" /> {t('solide:transactionsPage.modal.client')}
                  </label>
                  <div>
                    <p className="text-xs font-bold text-gray-900 truncate">{selectedTransaction.client_nom}</p>
                    <p className="text-[10px] text-gray-500 flex items-center gap-1">
                      <Smartphone className="h-2.5 w-2.5" /> {selectedTransaction.client_telephone}
                    </p>
                  </div>
                </div>

                {/* Agent */}
                <div className="space-y-0.5">
                  <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                    <UserCheck className="h-2.5 w-2.5" /> {t('solide:transactionsPage.modal.agent')}
                  </label>
                  <div className="flex items-center gap-1.5">
                    <div className="h-5 w-5 rounded-full bg-blue-50 flex items-center justify-center text-[9px] font-bold text-blue-600 border border-blue-100">
                      {selectedTransaction.agent?.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-xs font-medium text-gray-700 truncate">{selectedTransaction.agent || 'N/A'}</span>
                  </div>
                </div>

                {/* Opérateur */}
                <div className="space-y-0.5">
                  <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                    <User className="h-2.5 w-2.5" /> {t('solide:transactionsPage.modal.operator')}
                  </label>
                  <p className="text-xs text-gray-700">
                    {selectedTransaction.fait_par || 'Système'}
                  </p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsOpen(false)}>{t('solide:transactionsPage.modal.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <NewDepotModal
        open={newDepotOpen}
        onOpenChange={setNewDepotOpen}
        onSuccess={() => {
          fetchTransactions();
          // Optionnel: Afficher un toast ou une notif
        }}
      />
    </div>
  );
};

export default TransactionsBS;
